import { isFreeEmailDomain } from '@insightai/auth'
import { useCallback, useEffect, useRef, useState } from 'react'
import { config } from '../config'
import { mockCheckDomain } from './mockAuth'

export type DomainState =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'valid'; existingWorkspace?: string }
  | { status: 'invalid'; message: string }

const REASON_MESSAGES: Record<string, string> = {
  FREE_EMAIL_PROVIDER: 'Use your work email address — personal domains are not supported.',
  DISPOSABLE_DOMAIN: 'That looks like a temporary email address. Use your work email.',
  MALFORMED: 'Enter a valid email address.',
  BLOCKED: 'We cannot create an account for that domain. Contact us if this is unexpected.',
}

/**
 * M00-18 — instant work-email domain validation.
 *
 * Two layers, deliberately:
 *   1. A local check against a known free-mail list gives an instant answer for
 *      the overwhelmingly common case, with no network round-trip.
 *   2. A debounced server check catches disposable and blocked domains, which the
 *      client cannot know about, and reports whether the domain already maps to a
 *      workspace — useful, since the user should join it rather than create a
 *      second one.
 *
 * STALENESS is guarded with a monotonic request sequence rather than
 * AbortController. Someone typing an address fires several checks, and the only
 * correctness requirement is that an older response can never overwrite a newer
 * verdict. A sequence number does that in two lines and works identically in every
 * runtime; `AbortSignal` does not, because jsdom's implementation is rejected by
 * Node's fetch and the resulting TypeError is indistinguishable from a network
 * failure — which sent this through the fail-open path and silently discarded a
 * correct answer. Cancelling the socket for a ~200-byte debounced POST is not worth
 * that class of bug.
 *
 * The failure path fails OPEN: the server revalidates on submit, so a flaky
 * connection must never prevent someone from creating an account.
 */
export function useDomainValidation(email: string, { debounceMs = 400 } = {}) {
  const [state, setState] = useState<DomainState>({ status: 'idle' })
  const sequenceRef = useRef(0)

  const check = useCallback(async (value: string) => {
    const trimmed = value.trim()
    const sequence = ++sequenceRef.current
    const isCurrent = () => sequenceRef.current === sequence

    if (!trimmed || !trimmed.includes('@') || trimmed.endsWith('@')) {
      setState({ status: 'idle' })
      return
    }

    /**
     * Prototype mode: the fixture answers, and free-mail addresses are accepted.
     *
     * The local free-mail check is skipped rather than reordered — it is the one
     * rule that would still reject a dummy address before any mock was consulted,
     * which is exactly the friction prototype mode exists to remove. The staleness
     * guard still applies: the fixture path is debounced and awaited like the
     * network one, so an older verdict must not overwrite a newer one.
     */
    if (config.prototypeMode) {
      setState({ status: 'checking' })
      const verdict = await mockCheckDomain(trimmed)
      if (!isCurrent()) return
      setState(
        verdict.valid
          ? { status: 'valid', existingWorkspace: verdict.existingWorkspace }
          : {
              status: 'invalid',
              message: REASON_MESSAGES[verdict.reason] ?? 'That email address cannot be used.',
            },
      )
      return
    }

    if (isFreeEmailDomain(trimmed)) {
      setState({ status: 'invalid', message: REASON_MESSAGES.FREE_EMAIL_PROVIDER! })
      return
    }

    setState({ status: 'checking' })

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/auth/domain-check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      })
      if (!response.ok) throw new Error(`domain-check failed: ${response.status}`)

      const data = (await response.json()) as {
        valid: boolean
        reason?: string
        existing_workspace?: string
      }

      if (!isCurrent()) return

      setState(
        data.valid
          ? { status: 'valid', existingWorkspace: data.existing_workspace }
          : {
              status: 'invalid',
              message: REASON_MESSAGES[data.reason ?? ''] ?? 'That email address cannot be used.',
            },
      )
    } catch {
      if (!isCurrent()) return
      setState({ status: 'valid' })
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => void check(email), debounceMs)
    return () => clearTimeout(timer)
  }, [email, debounceMs, check])

  return { state, checkNow: () => void check(email) }
}
